import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Phone } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { brand } from "../data/site";
import { getPost, relatedPosts, categoryName } from "../data/blog";

const fmt = (iso) =>
  new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

// **bold** inside a bullet, without pulling in a markdown dependency for the
// one thing we actually use.
const inline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="text-ink font-semibold">{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );

const Block = ({ b }) => {
  if (b.h) {
    return (
      <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tight mt-12 mb-4 scroll-mt-28">
        {b.h}
      </h2>
    );
  }
  if (b.p) return <p className="text-muted-foreground leading-relaxed mb-5">{inline(b.p)}</p>;
  if (b.ul) {
    return (
      <ul className="space-y-3 mb-6">
        {b.ul.map((li, i) => (
          <li key={i} className="flex gap-3 text-muted-foreground leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0 mt-2.5" aria-hidden="true" />
            <span>{inline(li)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (b.note) {
    return (
      <aside className="border-l-4 border-brand bg-secondary rounded-r-xl px-6 py-5 my-8">
        <p className="text-ink/80 leading-relaxed text-[15px]">{inline(b.note)}</p>
      </aside>
    );
  }
  if (b.table) {
    return (
      <div className="overflow-x-auto my-8 rounded-xl border border-ink/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-ink text-white text-left">
              {b.table.head.map((h) => (
                <th key={h} className="font-heading uppercase tracking-wide text-xs px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.table.rows.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-secondary/60" : "bg-card"}>
                {r.map((c, j) => (
                  <td key={j} className={`px-4 py-3 align-top ${j === 0 ? "text-ink font-medium" : "text-muted-foreground"}`}>
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/resources" replace />;

  const url = `${SITE_URL}/resources/${post.slug}`;
  const related = relatedPosts(post.slug);

  // Article + FAQPage together: the first is what a search engine ranks, the
  // second is what an answer engine quotes.
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        headline: post.title,
        description: post.description,
        datePublished: post.date,
        dateModified: post.updated || post.date,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        author: { "@type": "Organization", name: brand.name, url: SITE_URL },
        publisher: {
          "@type": "Organization",
          name: brand.legalName,
          url: SITE_URL,
          logo: { "@type": "ImageObject", url: `${SITE_URL}${brand.logo}` },
        },
        articleSection: categoryName(post.category),
        isAccessibleForFree: true,
      },
      {
        "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE_URL}/resources` },
          { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
      },
    ],
  };

  return (
    <div>
      <SEO title={`${post.title} | Pepla Roofing`} description={post.description} jsonLd={jsonLd} />

      <article>
        <header className="pt-36 pb-12 bg-secondary border-b border-ink/10">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 text-xs text-ink/50 hover:text-brand transition-colors mb-7"
            >
              <ArrowLeft size={14} /> All resources
            </Link>
            <span className="kicker block">{categoryName(post.category)}</span>
            <h1 className="font-heading text-3xl md:text-5xl uppercase tracking-tight mt-3 mb-5">{post.title}</h1>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">{post.description}</p>
            <p className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink/45">
              <span>{fmt(post.date)}</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={13} aria-hidden="true" /> {post.read} min read
              </span>
              <span>Pepla Roofing · Phoenix, AZ</span>
            </p>
          </div>
        </header>

        <div className="py-14 bg-background">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            {/* The lift-ready summary. An answer engine that reads nothing else
                should still be able to quote this correctly. */}
            {post.takeaway?.length > 0 && (
              <div className="bg-ink text-white rounded-2xl p-7 mb-12">
                <h2 className="font-heading text-lg uppercase tracking-wide mb-4">The short version</h2>
                <ul className="space-y-3">
                  {post.takeaway.map((t, i) => (
                    <li key={i} className="flex gap-3 text-white/80 text-[15px] leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0 mt-2.5" aria-hidden="true" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {post.body.map((b, i) => <Block key={i} b={b} />)}

            {/* FAQ — rendered for people, emitted as schema for machines */}
            <section className="mt-16 pt-10 border-t border-ink/10">
              <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tight mb-7">
                Questions people ask
              </h2>
              <dl className="divide-y divide-ink/10">
                {post.faqs.map((f) => (
                  <div key={f.q} className="py-6">
                    <dt className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</dt>
                    <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {post.links?.length > 0 && (
              <section className="mt-12">
                <h2 className="font-heading text-xl uppercase tracking-wide mb-4">Related on this site</h2>
                <ul className="flex flex-wrap gap-2">
                  {post.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="inline-block text-sm border border-ink/15 rounded-full px-4 py-2 text-ink/70 hover:border-brand hover:text-brand transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </article>

      <section className="py-16 section-dark text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-4">
            Want Your Roof Looked At?
          </h2>
          <p className="text-white/70 mb-8">
            Free inspection, photographs and a written scope — yours whether or not you hire us.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
            >
              Free Inspection <ArrowRight size={18} />
            </Link>
            <a
              href={`tel:${brand.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors"
            >
              <Phone size={16} /> {brand.phone}
            </a>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 bg-background border-t border-ink/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="font-heading text-2xl uppercase tracking-wide mb-8">Keep reading</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/resources/${p.slug}`}
                  className="group card-lift bg-card border border-ink/10 rounded-2xl p-6"
                >
                  <span className="kicker">{categoryName(p.category)}</span>
                  <h3 className="font-heading text-lg uppercase tracking-wide mt-3 mb-2 group-hover:text-brand transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{p.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
